<script setup lang="ts">
import { ref, computed } from 'vue'
import {
    IonModal,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonButton,
    IonIcon,
    IonProgressBar,
    IonText
} from '@ionic/vue'
import { cloudDownloadOutline, refreshOutline, phonePortraitOutline, checkmarkCircleOutline } from 'ionicons/icons'
import { Capacitor } from '@capacitor/core'
import { useI18n } from 'vue-i18n'
import appUpdateService, { type UpdateInfo } from '@/services/update/app-update'

const { t } = useI18n()

const props = defineProps<{
    isOpen: boolean
    updateInfo: UpdateInfo | null
    webUpdateAvailable: boolean
    nativeUpdateAvailable: boolean
}>()

const emit = defineEmits<{
    (e: 'dismiss'): void
}>()

const isDownloading = ref(false)
const downloadComplete = ref(false)
const downloadProgress = ref(0)
const downloadError = ref<string | null>(null)
const downloadType = ref<'web' | 'native' | null>(null)

const canDismiss = computed(() => {
    if (isDownloading.value) return false
    if (downloadComplete.value) return false
    if (props.updateInfo?.forceUpdate) return false
    return true
})

async function handleWebUpdate() {
    if (!props.updateInfo) return

    isDownloading.value = true
    downloadProgress.value = 0
    downloadError.value = null
    downloadType.value = 'web'

    const unsubscribe = appUpdateService.addDownloadProgressListener((percent) => {
        downloadProgress.value = percent / 100
    })

    try {
        await appUpdateService.applyWebUpdate(
            props.updateInfo.webBundleUrl,
            props.updateInfo.webVersion
        )
        // Bundle queued — show restart prompt instead of exiting immediately
        downloadComplete.value = true
    } catch (error) {
        downloadError.value = t('update.downloadFailed')
        console.error('[AppUpdateModal] Web update failed:', error)
    } finally {
        isDownloading.value = false
        downloadType.value = null
        unsubscribe()
    }
}

async function handleNativeUpdate() {
    if (!props.updateInfo) return

    isDownloading.value = true
    downloadProgress.value = 0
    downloadError.value = null
    downloadType.value = 'native'

    const unsubscribe = appUpdateService.addDownloadProgressListener((percent) => {
        downloadProgress.value = percent / 100
    })

    try {
        await appUpdateService.downloadAndInstallApk(props.updateInfo.apkUrl)
    } catch (error) {
        downloadError.value = t('update.downloadFailed')
        console.error('[AppUpdateModal] APK download failed:', error)
    } finally {
        isDownloading.value = false
        downloadType.value = null
        unsubscribe()
    }
}

function handleRestart() {
    appUpdateService.exitApp()
}

function handleDismiss() {
    if (!canDismiss.value) return
    emit('dismiss')
}
</script>

<template>
    <IonModal
        :is-open="isOpen"
        :can-dismiss="canDismiss"
        :backdrop-dismiss="canDismiss"
        class="update-modal"
        @did-dismiss="handleDismiss"
    >
        <IonHeader>
            <IonToolbar>
                <IonTitle>{{ t('update.title') }}</IonTitle>
            </IonToolbar>
        </IonHeader>

        <IonContent class="ion-padding">
            <div class="update-content">
                <!-- Changelog -->
                <div v-if="updateInfo?.changelog" class="changelog-section">
                    <IonText color="medium">
                        <p class="changelog-label">{{ t('update.whatsNew') }}</p>
                    </IonText>
                    <p class="changelog-text">{{ updateInfo.changelog }}</p>
                </div>

                <!-- Download progress -->
                <div v-if="isDownloading" class="progress-section">
                    <IonText color="medium">
                        <p class="progress-label">
                            {{ downloadType === 'web' ? t('update.downloadingBundle') : t('update.downloadingApk') }}
                        </p>
                    </IonText>
                    <IonProgressBar :value="downloadProgress" color="primary" />
                    <IonText color="medium">
                        <p class="progress-percent">{{ Math.round(downloadProgress * 100) }}%</p>
                    </IonText>
                </div>

                <!-- Download complete — restart prompt -->
                <div v-else-if="downloadComplete" class="restart-section">
                    <IonIcon :icon="checkmarkCircleOutline" color="success" class="restart-icon" />
                    <IonText>
                        <p class="restart-message">{{ t('update.readyToRestart') }}</p>
                    </IonText>
                    <IonButton expand="block" color="success" @click="handleRestart">
                        <IonIcon slot="start" :icon="refreshOutline" />
                        {{ t('update.restartNow') }}
                    </IonButton>
                </div>

                <!-- Normal state: buttons and errors -->
                <template v-else>
                    <!-- Error message -->
                    <div v-if="downloadError" class="error-section">
                        <IonText color="danger">
                            <p>{{ downloadError }}</p>
                        </IonText>
                    </div>

                    <!-- Web update button -->
                    <div v-if="webUpdateAvailable && !nativeUpdateAvailable" class="action-section">
                        <IonButton expand="block" @click="handleWebUpdate">
                            <IonIcon slot="start" :icon="refreshOutline" />
                            {{ t('update.updateApp') }}
                            <IonText color="light" class="version-badge">
                                v{{ updateInfo?.webVersion }}
                            </IonText>
                        </IonButton>
                    </div>

                    <!-- Native update button (Android only) -->
                    <div v-if="nativeUpdateAvailable && Capacitor.getPlatform() === 'android'" class="action-section">
                        <IonButton expand="block" color="secondary" @click="handleNativeUpdate">
                            <IonIcon slot="start" :icon="cloudDownloadOutline" />
                            {{ t('update.installNewVersion') }}
                            <IonText color="light" class="version-badge">
                                v{{ updateInfo?.nativeVersion }}
                            </IonText>
                        </IonButton>
                        <IonText color="medium">
                            <p class="install-hint">
                                <IonIcon :icon="phonePortraitOutline" />
                                {{ t('update.apkInstallHint') }}
                            </p>
                        </IonText>
                    </div>

                    <!-- Later button -->
                    <div v-if="canDismiss" class="action-section">
                        <IonButton expand="block" fill="clear" color="medium" @click="handleDismiss">
                            {{ t('update.later') }}
                        </IonButton>
                    </div>
                </template>
            </div>
        </IonContent>
    </IonModal>
</template>

<style scoped lang="scss">
@import '@/assets/styles/variables';

.update-content {
    display: flex;
    flex-direction: column;
    gap: $spacing-md;
    padding: $spacing-sm 0;
}

.changelog-section {
    background: var(--warden-surface);
    border-radius: $border-radius-md;
    padding: $spacing-md;
}

.changelog-label {
    margin: 0 0 $spacing-xs;
    font-weight: 600;
    font-size: $font-size-sm;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.changelog-text {
    margin: 0;
    font-size: $font-size-base;
    line-height: 1.5;
    white-space: pre-line;
}

.progress-section {
    text-align: center;
}

.progress-label {
    margin: 0 0 $spacing-sm;
    font-size: $font-size-sm;
}

.progress-percent {
    margin: $spacing-xs 0 0;
    font-size: $font-size-sm;
    font-weight: 600;
}

.restart-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $spacing-md;
    padding: $spacing-lg $spacing-sm;
}

.restart-icon {
    font-size: 48px;
}

.restart-message {
    margin: 0;
    font-size: $font-size-base;
    text-align: center;
    line-height: 1.5;
}

.error-section {
    text-align: center;
    padding: $spacing-sm;
}

.action-section {
    margin-top: $spacing-xs;
}

.version-badge {
    margin-left: $spacing-sm;
    font-size: $font-size-xs;
    opacity: 0.8;
}

.install-hint {
    display: flex;
    align-items: center;
    gap: $spacing-xs;
    margin: $spacing-sm 0 0;
    font-size: $font-size-xs;
    text-align: center;
    justify-content: center;
}

.update-modal {
    --max-width: 500px;
}
</style>
